import React, { useState } from "react";
import { FOUNDER_HEADSHOT_URL } from "./constants";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * "Meet the Founder" section. Two-paragraph teaser on mount; Read More
 * smoothly reveals the rest. Mobile stacks (image above bio).
 */

const PARAGRAPHS = [
  // Visible by default ↓
  "Sara Love is the founder of Sister Signal, a nonprofit initiative dedicated to helping women recognise, navigate, and recover from emotional abuse, coercive control, domestic violence, and unsafe relationship dynamics.",
  "A survivor of long-term abusive relationships, Sara created Sister Signal after recognising a critical gap in accessible support for women who are questioning their experiences but may not yet be ready or able to seek traditional services. Her vision was to build the resource she wished had existed during her own journey — one that combines education, awareness, practical tools, and compassionate guidance in a format available anytime and anywhere.",
  // Revealed when expanded ↓
  "Sara holds a Bachelor's degree from the University of Iowa and spent more than a decade working in customer service and corporate environments. Although she did not come from a traditional technology background, she taught herself modern AI and no-code development tools in order to bring Sister Signal to life. Within weeks of beginning the project, she designed and launched the platform, developed a bilingual roadmap, established a nonprofit organization, and began building partnerships with domestic violence advocates, community organizations, and funders.",
  "Her inspiration comes not only from her own experiences, but also from witnessing the impact of abuse throughout her family, community, and online survivor networks. Over the years, she has watched countless women struggle to identify coercive control, access reliable information, or find support before situations escalated into crisis. These observations reinforced her belief that awareness, education, and community connection can help prevent harm and support survivors earlier in their journeys.",
  "In addition to her nonprofit work, Sara is an advocate for animal welfare and participates in community cat care and trap-neuter-return (TNR) efforts in her neighborhood.",
  "Through Sister Signal, Sara's mission is simple: to ensure that no woman has to face confusion, isolation, or abuse alone. By combining technology, education, and survivor-informed design, she is building accessible tools that help women move from uncertainty to clarity, safety, healing, and long-term empowerment.",
];

const VISIBLE_COUNT = 2;

export default function FounderSection() {
  const [expanded, setExpanded] = useState(false);
  const visible = PARAGRAPHS.slice(0, VISIBLE_COUNT);
  const hidden = PARAGRAPHS.slice(VISIBLE_COUNT);

  return (
    <section
      className="max-w-5xl mx-auto px-6 sm:px-10 py-16"
      data-testid="founder-section"
    >
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Headshot */}
        <div className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-[2rem] overflow-hidden bg-brand-soft/60 shadow-[0_30px_60px_-20px_rgba(214,90,140,0.25)]">
            <img
              src={FOUNDER_HEADSHOT_URL}
              alt="Sara Love, Founder of Sister Signal"
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid="founder-headshot"
            />
          </div>
        </div>

        {/* Bio */}
        <div className="lg:col-span-7">
          <p className="font-script text-2xl text-brand">meet the founder</p>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl leading-tight">Sara Love</h2>
          <p className="mt-1 text-sm uppercase tracking-widest text-foreground/55">Founder, Sister Signal</p>

          <div className="mt-6 space-y-5 text-foreground/85 leading-relaxed">
            {visible.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            {/* Animated reveal — height transition + opacity */}
            <div
              className={`grid transition-all duration-500 ease-in-out ${
                expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
              data-testid="founder-bio-extended"
              aria-hidden={!expanded}
            >
              <div className="overflow-hidden space-y-5">
                {hidden.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>

          <button
            data-testid="founder-toggle"
            onClick={() => setExpanded((v) => !v)}
            className="mt-6 inline-flex items-center gap-1 text-sm text-brand hover:underline underline-offset-4"
          >
            {expanded ? <>Read less <ChevronUp className="w-4 h-4" /></> : <>Read more <ChevronDown className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </section>
  );
}
